import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCurrentLanguage } from '../../utils/navigation';
import authService from '../../api/auth';
import {
  Box, TextField, Button, Typography, Container, Alert, CircularProgress
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, MarkEmailRead as MarkEmailReadIcon } from '@mui/icons-material';
import AuthNavbar from '../../components/layout/AuthNavbar';

const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const lang = getCurrentLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async () => {
    if (!email.trim()) {
      setError(t('auth.forgotPassword.emailRequired'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await authService.forgotPassword(email.trim(), lang);
      setSent(true);
    } catch (err) {
      const code = err.code || err.message;
      if (code === 'auth/user-not-found') {
        setError(t('auth.forgotPassword.userNotFound'));
      } else if (code === 'auth/invalid-email') {
        setError(t('auth.forgotPassword.invalidEmail'));
      } else {
        setError(t('auth.forgotPassword.sendFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AuthNavbar />
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#FAFAFA', pt: 8 }}>
        <Container maxWidth="sm">
          <Box sx={{
            bgcolor: '#fff', borderRadius: 3,
            boxShadow: '0 2px 20px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0',
            p: { xs: 3, sm: 5 }, mx: 'auto', maxWidth: 440
          }}>
            {sent ? (
              <Box sx={{ textAlign: 'center' }}>
                <MarkEmailReadIcon sx={{ fontSize: 56, color: '#F5A623', mb: 2 }} />
                <Typography variant="h6" fontWeight={700} color="#1a1a1a" mb={1}>
                  {t('auth.forgotPassword.checkEmail')}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  {t('auth.forgotPassword.sentTo')} <strong>{email}</strong>
                  {'. '}{t('auth.forgotPassword.followLink')}
                </Typography>
                <Button
                  fullWidth variant="contained"
                  component={Link} to={`/${lang}/auth/login`}
                  sx={{ py: 1.4, fontWeight: 600, borderRadius: 2.5, bgcolor: '#F5A623', '&:hover': { bgcolor: '#e09500' } }}
                >
                  {t('auth.forgotPassword.backToLogin')}
                </Button>
              </Box>
            ) : (
              <>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Box component="img" src="/images/lingoply-logo.jpg" alt="Lingoply"
                    sx={{ width: 64, height: 64, borderRadius: '50%', mb: 1.5 }} />
                  <Typography variant="h5" fontWeight={700} color="#1a1a1a">
                    {t('auth.forgotPassword.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('auth.forgotPassword.subtitle')}
                  </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <TextField
                  fullWidth required
                  label={t('auth.emailAddress')}
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                  sx={{ mb: 3 }}
                />

                <Button
                  fullWidth variant="contained"
                  disabled={loading}
                  onClick={handleSubmit}
                  sx={{ py: 1.5, mb: 2, fontSize: '1rem', fontWeight: 600, borderRadius: 2.5, bgcolor: '#F5A623', '&:hover': { bgcolor: '#e09500' } }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : t('auth.forgotPassword.sendLink')}
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                  <Link to={`/${lang}/auth/login`} style={{ textDecoration: 'none', color: '#F5A623', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <ArrowBackIcon fontSize="small" />
                    {t('auth.forgotPassword.backToLogin')}
                  </Link>
                </Box>
              </>
            )}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default ForgotPasswordPage;
